import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { CHANNEL_ORDER, CHANNELS, type ChannelId } from "../../../shared/goals/channels";
import { GoalConfigSchema, type GoalConfig } from "../../../shared/goals/config";
import { mergePatch } from "../../../shared/goals/patch";
import { useGoBack } from "../../../shared/phone/history";
import { scrollToTop, useNative } from "../../../shared/phone/media";
import { PhoneNav } from "../../../shared/phone/PhoneNav";
import { PnRow, SaveBar } from "../../../shared/phone/Row";
import { ActionSheet, Sheet } from "../../../shared/phone/Sheet";
import { Icon } from "../../components/ui/Icon";
import { Inline } from "../../components/ui/Rich";
import { api } from "../../lib/api";
import { ccApi, type Results } from "../../lib/ccApi";
import { goalsApi, type GoalView } from "../../lib/goalsApi";
import { libraryApi } from "../../lib/libraryApi";
import type { SavePatch } from "../goal/ChannelsStep";
import { ChannelsStep, channelTitle } from "../goal/ChannelsStep";
import { FlowHead, type ProductRef } from "../goal/FlowHead";
import { GoalStep, type GoalValues } from "../goal/GoalStep";
import { formOf, goalPatch } from "../goal/goalForm";
import { StepHead } from "../goal/Native";
import { PlaybookStep } from "../goal/PlaybookStep";
import { Highlighted } from "../goal/PlanReview";
import { andList } from "../goal/text";
import { changes, effects, problems, summary, type Change } from "./editDiff";
import { pace, totalSpent, usd } from "./text";
import { errText, useCcToast } from "./useCc";
import { useCan } from "../../context/AuthContext";
import "../../styles/wizard.css";
import "../../styles/cc.css";

const NAMES = ["", "goal", "channel", "playbook", "changes"];
const TITLES: Record<number, [string, string?]> = {
  1: ["Change the goal", "Say what should happen, how many, and by when."],
  3: ["The playbook"],
  4: ["Review the changes", "Nothing changes until you apply them."],
};
const titleOf = (step: number, config: GoalConfig | null): [string, string?] => (step === 2 ? [channelTitle(config)] : (TITLES[step] ?? TITLES[1]));

/** Edits on a running goal not applied yet, per goal and version, so leaving a step and coming back keeps them. */
const drafts = new Map<string, GoalConfig>();

function useProduct(productId: string): ProductRef | null {
  const [p, setP] = useState<ProductRef | null>(null);
  useEffect(() => {
    api.products.list().then(
      (r) => {
        const x = r.products.find((y) => y.id === productId);
        setP(x ? { id: x.id, name: x.name, logoR2Key: x.logoR2Key, coldEmail: x.coldEmail } : null);
      },
      () => setP(null),
    );
  }, [productId]);
  return p;
}

interface Props {
  view: GoalView;
  reload: () => Promise<void>;
}

/**
 * Edit goal (`/goals/:id/edit`): the setup's own steps in edit mode. A running goal ends on its changes and
 * Apply makes the next plan version; a goal approved but not live is planned again and re-approved, with
 * Get ready kept. It opens on the step that holds the change (`?step=`) and goes back where it started.
 */
export function EditGoal(p: Props) {
  const running = p.view.goal.state === "live" || p.view.goal.state === "paused";
  return running ? <EditRunning {...p} /> : <EditReplan {...p} />;
}

function useSteps(max: number) {
  const [params, setParams] = useSearchParams();
  // Why: links the agent wrote before the step was renamed say "channels".
  const asked = params.get("step") === "channels" ? "channel" : (params.get("step") ?? "goal");
  const step = Math.min(max, Math.max(1, NAMES.indexOf(asked) || 1));
  const toStep = (i: number) => {
    const next = new URLSearchParams(params);
    next.set("step", NAMES[i] ?? "goal");
    next.delete("run");
    setParams(next);
    window.scrollTo(0, 0);
  };
  /** The run drafting the playbook, kept in the address so a reload keeps waiting for it. */
  const onRun = useCallback(
    (run: string | null) =>
      setParams(
        (p) => {
          const next = new URLSearchParams(p);
          if (run) next.set("run", run);
          else next.delete("run");
          return next;
        },
        { replace: true },
      ),
    [setParams],
  );
  return { params, setParams, step, toStep, onRun };
}

// --- On a phone and an iPad: the steps as rows, each opening in its own screen ------------------------

type StepName = "goal" | "channel" | "playbook";
const GOAL_LINE = "Say what should happen, how many, and by when.";
const CHANNEL_LINE = "What the agent does on its own, and what it asks you first.";

/**
 * Which step is open on a phone: none (the steps as rows) or one, pushed over them. Why: the address keeps
 * `?step=` the way the computer's wizard reads it, so the agent's links open the same step on both.
 */
function useNativeStep() {
  const [params, setParams] = useSearchParams();
  const raw = params.get("step");
  const name = raw === "channels" ? "channel" : raw;
  const open = (step: StepName) => {
    const next = new URLSearchParams(params);
    next.set("step", step);
    next.delete("run");
    setParams(next);
    scrollToTop();
  };
  const kept = new URLSearchParams(params);
  kept.delete("step");
  kept.delete("run");
  const q = kept.toString();
  return { name, open, search: q ? `?${q}` : "" };
}

/** The goal's playbook as the library names it. */
function usePlaybookTitle(itemId: string | null) {
  const [title, setTitle] = useState<string | null>(null);
  useEffect(() => {
    if (!itemId) return;
    let live = true;
    libraryApi.item(itemId).then(
      (r) => live && setTitle(r.item.title),
      () => live && setTitle(null),
    );
    return () => {
      live = false;
    };
  }, [itemId]);
  return title;
}

const city = (m: string) => m.split(",")[0].trim();

function channelSub(config: GoalConfig, k: ChannelId): string {
  const c = config.channels[k];
  if (k === "email") return c.markets.length ? andList(c.markets.map(city)) : "No market yet";
  if (c.rules.length) return c.rules.length === 1 ? "1 rule of yours" : `${c.rules.length} rules of yours`;
  return c.card ? "Your edits" : CHANNELS[k].sub;
}

/** A row's value, marked once it changed. */
const value = (changed: boolean, v?: string) =>
  changed || v ? (
    <span className="su-vv">
      {changed && <span className="pn-pill violet">Changed</span>}
      {v}
    </span>
  ) : undefined;

/** What is set, as rows: the goal and its money, the channel and the playbook, each marked once it changed. */
function EditRows({ config, ch, pbTitle, onOpen, note }: { config: GoalConfig; ch: Change[]; pbTitle: string | null; onOpen: (s: StepName) => void; note: string }) {
  const has = (...ks: Change["k"][]) => ch.some((c) => ks.includes(c.k));
  const pin = config.library.find((p) => p.kind === "playbook") ?? null;
  return (
    <div className="pn-stack">
      <section>
        <div className="pn-cap">Goal</div>
        <div className="pn-grp">
          <PnRow
            onClick={() => onOpen("goal")}
            title="The goal"
            sub={
              <span className="su-ghl">
                <Highlighted text={config.sentence} />
              </span>
            }
            value={value(has("goal", "by", "text"))}
            wrap
            chevron
          />
          <PnRow onClick={() => onOpen("goal")} title="Total budget" value={value(has("budget"), usd(config.money.budgetCents))} chevron />
          <PnRow onClick={() => onOpen("goal")} title="Most per result" value={value(has("max"), usd(config.money.perResultMaxCents))} chevron />
        </div>
      </section>
      <section>
        <div className="pn-cap">Channel</div>
        <div className="pn-grp">
          {CHANNEL_ORDER.filter((k) => config.channels[k]?.on).map((k) => (
            <PnRow
              key={k}
              onClick={() => onOpen("channel")}
              title={CHANNELS[k].label}
              sub={channelSub(config, k)}
              value={value(ch.some((c) => (c.k === "card" && c.channel === k) || (k === "email" && (c.k === "mkAdd" || c.k === "mkRm"))))}
              chevron
            />
          ))}
        </div>
      </section>
      <section>
        <div className="pn-cap">Playbook</div>
        <div className="pn-grp">
          <PnRow onClick={() => onOpen("playbook")} title={pbTitle ?? "The playbook"} sub={pin ? `Version ${pin.version}, fitted to this goal` : "Not written for this goal yet"} value={value(has("pb", "pbSwap"))} chevron />
        </div>
      </section>
      <p className="pn-note">{note}</p>
    </div>
  );
}

/** A step opened from the rows: its own bar, back to the rows. */
function StepScreen({ title, rows, onBack, children }: { title: string; rows: string; onBack?: () => void; children: ReactNode }) {
  return (
    <div className="pn-screen">
      <PhoneNav title={title} back={{ to: rows, label: "Change the goal", onBack }} />
      {children}
    </div>
  );
}

// --- A running goal: the changes are made on a copy and applied as the next version -------------------

function EditRunning({ view, reload }: Props) {
  const navigate = useNavigate();
  const goalId = view.goal.id;
  const origin = `/goals/${encodeURIComponent(goalId)}`;
  const base = view.current?.config ?? null;
  const key = `${goalId}:${view.current?.version ?? 0}`;
  const [draft, setDraftState] = useState<GoalConfig | null>(() => drafts.get(key) ?? base);
  const setDraft = useCallback(
    (c: GoalConfig) => {
      drafts.set(key, c);
      setDraftState(c);
    },
    [key],
  );
  const [results, setResults] = useState<Results | null>(null);
  const [busy, setBusy] = useState(false);
  const { toast, say } = useCcToast();
  const product = useProduct(view.goal.productId);
  const { params, step, toStep, onRun } = useSteps(4);
  const moneyLocked = !useCan()("admin");
  const native = useNative();
  const nstep = useNativeStep();
  const goBack = useGoBack();
  const [review, setReview] = useState(false);
  const [undo, setUndo] = useState(false);
  const typed = useRef<GoalValues | null>(null);
  const pbTitle = usePlaybookTitle(native ? (draft?.library.find((p) => p.kind === "playbook")?.itemId ?? null) : null);

  useEffect(() => {
    ccApi.results(goalId).then(setResults, () => setResults(null));
  }, [goalId]);

  const save: SavePatch = useCallback(
    async (patch, ok) => {
      if (!draft) return false;
      const parsed = GoalConfigSchema.safeParse(mergePatch(draft, patch));
      if (!parsed.success) {
        say(`That change does not fit: ${parsed.error.issues[0]?.message ?? "invalid"}`, { done: false });
        return false;
      }
      setDraft(parsed.data);
      if (ok) say(ok);
      return true;
    },
    [draft, say, setDraft],
  );

  const ch = useMemo(() => (base && draft ? changes(base, draft) : []), [base, draft]);
  if (!base || !draft || !view.current)
    return native ? (
      <div className="pn-screen">
        <PhoneNav title="Change the goal" back={{ to: origin, label: "Goal" }} />
        <p className="pn-empty">This goal has no plan in force to edit.</p>
      </div>
    ) : (
      <div className="wrap ga gw">{<p className="load-note">This goal has no plan in force to edit.</p>}</div>
    );

  const spent = results ? totalSpent(results) : 0;
  const bad = problems(draft, spent);
  const p = results ? pace(results, base.result) : null;
  const fx = effects(base, draft, ch, { got: results?.totals.results ?? 0, weekly: p ? p.weekly : null, spentCents: spent, paused: view.goal.state === "paused" });

  const apply = async () => {
    if (!ch.length || bad.length || !view.current) return;
    setBusy(true);
    try {
      const { version } = await ccApi.apply(goalId, view.current.version, draft, summary(ch));
      drafts.delete(key);
      await reload();
      navigate(origin, { state: { said: `Applied as plan version ${version}.` } });
    } catch (e) {
      say(errText(e), { done: false });
      setBusy(false);
    }
  };

  if (native) {
    const rows = `${origin}/edit${nstep.search}`;
    const playbookSay = (m: string, ok?: boolean) => say(m, { done: ok !== false });
    if (nstep.name === "goal") {
      const leave = () => {
        if (typed.current) void save(goalPatch(typed.current, draft), "");
        typed.current = null;
        goBack(rows);
      };
      return (
        <StepScreen title="The goal" rows={rows} onBack={leave}>
          <GoalStep
            moneyLocked={moneyLocked}
            initial={formOf(draft)}
            busy={false}
            onValues={(v) => {
              typed.current = v;
            }}
            onNext={(v) => {
              typed.current = null;
              void save(goalPatch(v, draft), "").then((ok) => ok && goBack(rows));
            }}
            next="Done"
            head={<StepHead title="The goal" sub={GOAL_LINE} />}
          />
          {toast}
        </StepScreen>
      );
    }
    if (nstep.name === "channel")
      return (
        <StepScreen title="Channel" rows={rows}>
          <ChannelsStep config={draft} save={save} say={(m) => say(m)} onBack={() => goBack(rows)} onNext={() => goBack(rows)} busy={false} next="Done" head={<StepHead title={channelTitle(draft)} sub={CHANNEL_LINE} />} />
          {toast}
        </StepScreen>
      );
    if (nstep.name === "playbook")
      return (
        <StepScreen title="Playbook" rows={rows}>
          {product && (
            <PlaybookStep
              goalId={goalId}
              product={product}
              config={draft}
              runId={params.get("run")}
              onRun={onRun}
              save={save}
              say={playbookSay}
              onBack={() => goBack(rows)}
              onNext={() => goBack(rows)}
              next="Done"
              busy={false}
              head={<StepHead title="The playbook" sub={`How ${product.name} markets. Changes stay with this goal.`} />}
            />
          )}
          {toast}
        </StepScreen>
      );
    const reviewing = review || nstep.name === "changes";
    const closeReview = () => {
      setReview(false);
      if (nstep.name === "changes") navigate(rows, { replace: true });
    };
    const next = view.current.version + 1;
    return (
      <div className="pn-screen">
        <PhoneNav title="Change the goal" large back={{ to: origin, label: "Goal" }} sub={`Plan version ${view.current.version} is in force`} />
        <EditRows config={draft} ch={ch} pbTitle={pbTitle} onOpen={nstep.open} note={`Changes stay here until you apply them. Then they become plan version ${next}.`} />
        <SaveBar dirty={ch.length > 0} saving={busy} onSave={() => setReview(true)} onDiscard={() => setUndo(true)} label="Apply changes" />
        <ActionSheet
          open={undo}
          onClose={() => setUndo(false)}
          title="Undo your changes?"
          message="The goal stays as it is now."
          actions={[
            {
              label: "Undo the changes",
              danger: true,
              onSelect: () => {
                drafts.delete(key);
                setDraftState(base);
                say("Changes undone.");
              },
            },
          ]}
        />
        <Sheet
          open={reviewing && ch.length > 0}
          onClose={closeReview}
          tall
          title="Review the changes"
          left={
            <button type="button" className="pn-tbtn" onClick={closeReview}>
              Cancel
            </button>
          }
          foot={
            <>
              <button type="button" className="pn-btn pri wide" disabled={bad.length > 0 || busy} onClick={() => void apply()}>
                <Icon name="check" />
                Apply the changes
              </button>
              <p className="su-fnote">Becomes plan version {next}</p>
            </>
          }
        >
          {bad.length > 0 && (
            <section className="su-sheetsec">
              <div className="pn-cap su-warnc">Fix these first</div>
              <div className="pn-grp">
                {bad.map((x) => (
                  <PnRow key={x} icon={<Icon name="info" />} iconTone="warn" title={x} className="su-full" />
                ))}
                <PnRow
                  onClick={() => {
                    closeReview();
                    nstep.open("goal");
                  }}
                  title="Change the goal"
                  className="su-acc"
                  chevron
                />
              </div>
            </section>
          )}
          <section className="su-sheetsec">
            <div className="pn-cap">What you changed</div>
            <div className="pn-grp su-diff">
              {ch
                .flatMap((c) => c.lines)
                .map((l, i) => (
                  <div className={`su-dl ${l.kind}`} key={i}>
                    <span aria-hidden="true">{l.kind === "add" ? "+" : l.kind === "rm" ? "−" : "•"}</span>
                    {l.text}
                  </div>
                ))}
            </div>
          </section>
          <section className="su-sheetsec">
            <div className="pn-cap">What happens to what's running</div>
            <div className="pn-grp">
              {fx.map((t) => (
                <PnRow key={t} icon={<Icon name="check" />} iconTone="good" title={<Inline text={t} />} className="su-full" />
              ))}
            </div>
          </section>
        </Sheet>
        {toast}
      </div>
    );
  }

  const [title, sub] = titleOf(step, draft);
  return (
    <div className="wrap ga gw ccx">
      <FlowHead
        back={{ to: origin, label: "Goal" }}
        crumb="Edit goal"
        product={product}
        steps={["Goal", "Channel", "Playbook", "Changes"]}
        step={step}
        canGo={(i) => i < step}
        onGo={toStep}
        title={title}
        sub={sub}
        goalRef={draft.sentence}
      />
      {step === 1 && (
        <GoalStep
          moneyLocked={moneyLocked}
          initial={formOf(draft)}
          busy={false}
          onNext={(v) => {
            void save(goalPatch(v, draft), "").then((ok) => ok && toStep(2));
          }}
        />
      )}
      {step === 2 && <ChannelsStep config={draft} save={save} say={(m) => say(m)} onBack={() => toStep(1)} onNext={() => toStep(3)} busy={false} next="Next: the playbook" />}
      {step === 3 && product && (
        <PlaybookStep
          goalId={goalId}
          product={product}
          config={draft}
          runId={params.get("run")}
          onRun={onRun}
          save={save}
          say={(m, ok) => say(m, { done: ok !== false })}
          onBack={() => toStep(2)}
          onNext={() => toStep(4)}
          next="Review the changes"
          busy={false}
        />
      )}
      {step === 4 &&
        (!ch.length ? (
          <>
            <section className="card">
              <p className="ph-s" style={{ margin: 0 }}>
                You haven't changed anything yet.
              </p>
            </section>
            <div className="wz-nav">
              <span className="sp" />
              <button type="button" className="btn ghost" onClick={() => toStep(3)}>
                <Icon name="left" />
                Back
              </button>
              <button type="button" className="btn" onClick={() => navigate(origin)}>
                Done
              </button>
            </div>
          </>
        ) : (
          <>
            {bad.length > 0 && (
              <section className="card ed-card ed-bad">
                <div className="card-h">
                  <h3>Fix these first</h3>
                </div>
                <ul className="ed-list">
                  {bad.map((x) => (
                    <li key={x}>
                      <Icon name="info" />
                      <span>{x}</span>
                    </li>
                  ))}
                </ul>
                <div className="m-act">
                  <button type="button" className="btn sm ghost" onClick={() => toStep(1)}>
                    Change the goal
                  </button>
                </div>
              </section>
            )}
            <section className="card ed-card">
              <div className="card-h">
                <h3>What you changed</h3>
              </div>
              <div className="diff">
                {ch
                  .flatMap((c) => c.lines)
                  .map((l, i) => (
                    <div className={`dl ${l.kind}`} key={i}>
                      {l.text}
                    </div>
                  ))}
              </div>
            </section>
            <section className="card ed-card">
              <div className="card-h">
                <h3>What happens to what's running</h3>
              </div>
              <ul className="ed-list">
                {fx.map((t) => (
                  <li key={t}>
                    <Icon name="check" />
                    <span>
                      <Inline text={t} />
                    </span>
                  </li>
                ))}
              </ul>
            </section>
            <div className="wz-nav">
              <span className="sp">Becomes plan version {view.current.version + 1}</span>
              <button type="button" className="btn ghost" onClick={() => toStep(3)}>
                <Icon name="left" />
                Back
              </button>
              <button type="button" className="btn" disabled={bad.length > 0 || busy} style={bad.length || busy ? { opacity: 0.45 } : undefined} onClick={() => void apply()}>
                <Icon name="check" />
                Apply the changes
              </button>
            </div>
          </>
        ))}
      {toast}
    </div>
  );
}

// --- Approved but not live: the setup starts again from the version in force, then a new plan -------------

function EditReplan({ view, reload }: Props) {
  const goalId = view.goal.id;
  const origin = `/goals/${encodeURIComponent(goalId)}`;
  const product = useProduct(view.goal.productId);
  const { toast, say } = useCcToast();
  const { params, setParams, step, toStep, onRun } = useSteps(3);
  const moneyLocked = !useCan()("admin");
  const [config, setConfig] = useState<GoalConfig | null>(null);
  const [busy, setBusy] = useState(false);
  const started = useRef(false);
  const native = useNative();
  const nstep = useNativeStep();
  const goBack = useGoBack();
  const [undo, setUndo] = useState(false);
  const typed = useRef<GoalValues | null>(null);
  const pbTitle = usePlaybookTitle(native ? (config?.library.find((p) => p.kind === "playbook")?.itemId ?? null) : null);

  // Why: Edit goal starts from the plan in force; a reload keeps the edits already saved (`ed=1`).
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    if (params.get("ed") === "1") {
      setConfig(view.goal.setup);
      return;
    }
    ccApi.replan(goalId).then(
      async () => {
        const fresh = await goalsApi.get(goalId);
        setConfig(fresh.goal.setup);
        const next = new URLSearchParams(params);
        next.set("ed", "1");
        setParams(next, { replace: true });
      },
      (e) => say(errText(e), { done: false }),
    );
  }, [goalId, params, setParams, view.goal.setup, say]);

  const save: SavePatch = useCallback(
    async (patch, ok) => {
      try {
        const r = await goalsApi.patchSetup(goalId, patch);
        setConfig(r.config);
        if (ok) say(ok);
        return true;
      } catch (e) {
        say(errText(e), { done: false });
        return false;
      }
    },
    [goalId, say],
  );

  const plan = async () => {
    setBusy(true);
    try {
      await goalsApi.plan(goalId);
      await reload();
    } catch (e) {
      say(errText(e), { done: false });
      setBusy(false);
    }
  };

  if (native) {
    const rows = `${origin}/edit${nstep.search}`;
    if (!config)
      return (
        <div className="pn-screen">
          <PhoneNav title="Change the goal" back={{ to: origin, label: "Goal" }} />
          <p className="pn-empty">Opening the goal…</p>
          {toast}
        </div>
      );
    if (nstep.name === "goal") {
      const leave = () => {
        if (typed.current) void save(goalPatch(typed.current, config), "");
        typed.current = null;
        goBack(rows);
      };
      return (
        <StepScreen title="The goal" rows={rows} onBack={leave}>
          <GoalStep
            moneyLocked={moneyLocked}
            initial={formOf(config)}
            busy={busy}
            onValues={(v) => {
              typed.current = v;
            }}
            onNext={(v) => {
              typed.current = null;
              setBusy(true);
              void save(goalPatch(v, config), "").then((ok) => {
                setBusy(false);
                if (ok) goBack(rows);
              });
            }}
            next="Done"
            head={<StepHead title="The goal" sub={GOAL_LINE} />}
          />
          {toast}
        </StepScreen>
      );
    }
    if (nstep.name === "channel")
      return (
        <StepScreen title="Channel" rows={rows}>
          <ChannelsStep config={config} save={save} say={(m) => say(m)} onBack={() => goBack(rows)} onNext={() => goBack(rows)} busy={busy} next="Done" head={<StepHead title={channelTitle(config)} sub={CHANNEL_LINE} />} />
          {toast}
        </StepScreen>
      );
    if (nstep.name === "playbook")
      return (
        <StepScreen title="Playbook" rows={rows}>
          {product && (
            <PlaybookStep
              goalId={goalId}
              product={product}
              config={config}
              runId={params.get("run")}
              onRun={onRun}
              save={save}
              say={(m, ok) => say(m, { done: ok !== false })}
              onBack={() => goBack(rows)}
              onNext={() => goBack(rows)}
              next="Done"
              busy={busy}
              head={<StepHead title="The playbook" sub={`How ${product.name} markets. Changes stay with this goal.`} />}
            />
          )}
          {toast}
        </StepScreen>
      );
    const inForce = view.current?.config ?? null;
    const ch = inForce ? changes(inForce, config) : [];
    const version = view.current?.version ?? view.goal.version;
    return (
      <div className="pn-screen">
        <PhoneNav title="Change the goal" large back={{ to: origin, label: "Goal" }} sub={`Plan version ${version} is approved`} />
        <EditRows config={config} ch={ch} pbTitle={pbTitle} onOpen={nstep.open} note="The goal is not live yet, so the agent writes the plan again with your changes. You approve it before anything runs." />
        <SaveBar dirty={ch.length > 0} saving={busy} onSave={() => void plan()} onDiscard={() => setUndo(true)} label="Update the plan" />
        <ActionSheet
          open={undo}
          onClose={() => setUndo(false)}
          title="Undo your changes?"
          message={`The goal goes back to plan version ${version}.`}
          actions={[
            {
              label: "Undo the changes",
              danger: true,
              onSelect: () => {
                setBusy(true);
                ccApi
                  .replan(goalId)
                  .then(() => goalsApi.get(goalId))
                  .then(
                    (fresh) => {
                      setConfig(fresh.goal.setup);
                      say("Changes undone.");
                    },
                    (e) => say(errText(e), { done: false }),
                  )
                  .finally(() => setBusy(false));
              },
            },
          ]}
        />
        {toast}
      </div>
    );
  }

  const [title, sub] = titleOf(step, config);
  return (
    <div className="wrap ga gw ccx">
      <FlowHead
        back={{ to: origin, label: "Goal" }}
        crumb="Edit goal"
        product={product}
        steps={["Goal", "Channel", "Playbook", "Plan", "Review"]}
        step={step}
        canGo={(i) => i < step}
        onGo={toStep}
        title={title}
        sub={sub}
        goalRef={config?.sentence ?? view.goal.sentence}
      />
      {!config ? (
        <p className="load-note">Opening the goal…</p>
      ) : (
        <>
          {step === 1 && (
            <GoalStep
              moneyLocked={moneyLocked}
              initial={formOf(config)}
              busy={busy}
              onNext={(v) => {
                setBusy(true);
                void save(goalPatch(v, config), "").then((ok) => {
                  setBusy(false);
                  if (ok) toStep(2);
                });
              }}
            />
          )}
          {step === 2 && <ChannelsStep config={config} save={save} say={(m) => say(m)} onBack={() => toStep(1)} onNext={() => toStep(3)} busy={busy} next="Next: the playbook" />}
          {step === 3 && product && (
            <PlaybookStep
              goalId={goalId}
              product={product}
              config={config}
              runId={params.get("run")}
              onRun={onRun}
              save={save}
              say={(m, ok) => say(m, { done: ok !== false })}
              onBack={() => toStep(2)}
              onNext={() => void plan()}
              next="Update the plan"
              busy={busy}
            />
          )}
        </>
      )}
      {toast}
    </div>
  );
}
